import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Container from '@material-ui/core/Container'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Divider from '@material-ui/core/Divider'
import Tooltip from '@material-ui/core/Tooltip'
import CheckIcon from '@material-ui/icons/Check'
import ExploreOutlinedIcon from '@material-ui/icons/ExploreOutlined'
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined'
import { JsonLd } from '@components/atoms'
import defaults from '@config/defaults'
import { autocompleteCity } from '@lib/geo-api'
import * as amplitude from '@amplitude/analytics-browser'
import PgaCoachOfferingCard, { buildPgaCoachOfferingClickAnalyticsPayload } from '@components/pga-coach-offering-card'
import PgaCoachInfoPanel from '@components/pga-coach-info-panel'
import ExternallyBookableCoachCard from '@components/pga-coach-offering-card/externally-bookable-coach-card'
import PgaCoachSearchMap from '@components/pga-coach-search-map'
import {
  EMPTY_RESULTS,
  PGA_COACH_DISTANCE_OPTIONS,
  PGA_COACH_FORMAT_OPTIONS,
  PGA_COACH_JOURNEY_ANALYTICS,
  PGA_COACH_PERSONAS,
  PGA_COACH_PRICE_OPTIONS,
  PGA_COACH_PROGRAMS_SEARCH_PATHNAME,
  PGA_COACH_LANDING_GUIDES_SECTION_ID,
  PGA_COACH_SCHEDULE_OPTIONS,
} from '@lib/pga-coach/constants'
import useFeatureFlags from '@lib/feature-flags/useFeatureFlags'
import { flags } from '@lib/feature-flags/constants'
import {
  buildIndexableCoachSearchPathname,
  buildJourneySearchUrl,
  buildProgramsSeoPathnameFromCurrent,
  formatResultsHeadline,
  getDefaultJourneySearchState,
  getJourneyDistanceMiles,
  getJourneyPersona,
  hasUrlSpecifiedJourneySearchState,
  isUnscopedProgramsSearchPath,
  journeyUrlHasGeoQueryParams,
  loadJourneyLastSearchState,
  loadJourneySavedSearches,
  normalizeJourneySearchState,
  pathnameEmbedsPersona,
  removeJourneySavedSearch,
  saveJourneyLastSearchState,
  saveJourneySavedSearch,
} from '@lib/pga-coach/search-state'
import {
  buildGolfLessonsLandingJsonLd,
  buildGolfLessonsResultJsonLd,
  getGolfLessonsDocumentSeo,
  shouldNoIndexGolfLessonsDocument,
} from '@lib/pga-coach/search-seo'
import {
  detectJourneyLocationFromIp,
  JOURNEY_OFFERINGS_PAGE_SIZE,
  searchPgaCoachOfferings,
} from '@lib/pga-coach/offering-search'
import useDebounce from '@hooks/useDebounce'
import useOnScreen from '@hooks/useOnScreen'
import usePgaCoachSavedOfferings from '@hooks/usePgaCoachSavedOfferings'
import { OFFERING_FORMAT_ICON_COMPONENTS as FORMAT_CHIP_ICONS } from '@lib/pga-coach/offering-format-icons'
import {
  Bookmark as BookmarkIcon,
  Calendar as CalendarIcon,
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  CircleDollarSign as PriceIcon,
  Heart as HeartIcon,
  List as ListIcon,
  Map as MapIcon,
  MapPin as MapPinIcon,
  SlidersHorizontal as SlidersHorizontalIcon,
  X as XIcon,
  COACH_PROFILE_FILTER_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import {
  mapAlgoliaCityHitToLocationOption,
  resolveLocationOptionFromInput,
} from '@lib/pga-coach/resolve-journey-location'
import PrivateFormatChip from './PrivateFormatChip'
import useStyles from './pga-coach-search.styles'
import {
  computeFallbackOfferings,
  getAnalyticsFacilityIdFromOffering,
  getCoachSearchAutoExpandNoticeCopy,
  getCoachSearchEmptyAutoExpandAction,
  getCoachSearchEmptyAutoExpandLocationKey,
  getCoachSearchEmptyStateCopy,
  getCoachSearchProgressContent,
  isJourneySearchFullyRelaxed,
  isViewingOutsideSelectedArea,
  parseLatLng,
} from './utils'

/** Programs search: clears Algolia persona filter (shows offerings for any persona intent). */
const PERSONA_MENU_ALL_RESULTS = { id: '', label: 'All results' }
const MAP_DRAG_SEARCH_DEBOUNCE_MS = 500

const formatAroundLatLng = (coordinates) => {
  if (!coordinates) return ''
  const lat = Number(coordinates.lat)
  const lng = Number(coordinates.lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return ''
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`
}

function CoachFilterMenu({
  classes,
  anchorEl,
  onClose,
  options,
  selectedId,
  onSelectId,
  paperClassName,
  paperStyle,
  dividerAfterIndices,
}) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      keepMounted
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      getContentAnchorEl={null}
      PaperProps={{
        className: [classes.filterMenuPaper, paperClassName].filter(Boolean).join(' '),
        style: paperStyle,
      }}
    >
      {options.flatMap((option, index) => {
        const selected = selectedId === option.id
        const row = (
          <MenuItem
            key={option.id === '' ? '__persona_any__' : option.id}
            selected={selected}
            onClick={() => {
              onSelectId(option.id)
              onClose()
            }}
            className={classes.filterMenuItem}
            classes={{ selected: classes.filterMenuItemSelected }}
          >
            <ListItemIcon className={classes.filterMenuLeading}>
              {selected ? <CheckIcon fontSize="small" /> : <span className={classes.filterMenuLeadingSpacer} aria-hidden />}
            </ListItemIcon>
            <Typography component="span" variant="body2">{option.label}</Typography>
          </MenuItem>
        )
        if (dividerAfterIndices?.includes(index)) {
          return [
            row,
            <Divider
              key={`${option.id === '' ? '__persona_any__' : option.id}-section`}
              component="li"
              className={classes.filterMenuBetweenSections}
            />,
          ]
        }
        return [row]
      })}
    </Menu>
  )
}

function PgaCoachSearch({
  initialSearchState = {},
  initialResults = { hits: [] },
  initialError,
}) {
  const classes = useStyles()
  const router = useRouter()
  const hasHydratedSearchStorageRef = useRef(false)
  const lastViewResultsEventRef = useRef('')
  const loadMoreSentinelRef = useRef(null)
  const canLoadMoreOnVisibleRef = useRef(true)
  const autoExpandDistanceLocationKeyRef = useRef('')
  const autoShowBestLocationKeyRef = useRef('')
  const skipLastSearchPersistRef = useRef(false)
  const autoShowBestKeepCitySeoPathRef = useRef(false)
  const [searchState, setSearchState] = useState(normalizeJourneySearchState(initialSearchState))
  const [currentPage, setCurrentPage] = useState(1)
  const [locationInputValue, setLocationInputValue] = useState(initialSearchState.placeName || '')
  const [locationOptions, setLocationOptions] = useState([])
  const locationOptionsRef = useRef(locationOptions)
  locationOptionsRef.current = locationOptions
  const [results, setResults] = useState(initialResults)
  const [errorMessage, setErrorMessage] = useState(initialError || '')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isDetectingLocation, setIsDetectingLocation] = useState(!initialSearchState.placeName)
  const [saveMessage, setSaveMessage] = useState('')
  const [selectedOfferingId, setSelectedOfferingId] = useState(null)
  const [mapPinPopupAnchorOfferingId, setMapPinPopupAnchorOfferingId] = useState(null)
  const mapOpenDetailsNonceRef = useRef(0)
  const [mapOpenDetailsSignal, setMapOpenDetailsSignal] = useState(null)
  const [hoveredOfferingId, setHoveredOfferingId] = useState(null)
  const [mapViewport, setMapViewport] = useState(null)
  const [pendingMapCenter, setPendingMapCenter] = useState(null)
  const activePendingMapAroundLatLngRef = useRef('')
  const mapDragGeoInQueryRef = useRef(false)
  const explicitLocationInQueryRef = useRef(false)
  const suppressMapAutoFitRef = useRef(false)
  const [suppressMapAutoFit, setSuppressMapAutoFit] = useState(false)
  const [isMapUpdating, setIsMapUpdating] = useState(false)
  const [mobileView, setMobileView] = useState('results')
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [mapResetKey, setMapResetKey] = useState(0)
  const [savedSearches, setSavedSearches] = useState([])
  const [savedSearchAnchor, setSavedSearchAnchor] = useState(null)
  /** Desktop: when false, results are full width and the map column is hidden. */
  const [showDesktopMap, setShowDesktopMap] = useState(true)
  const [personaAnchor, setPersonaAnchor] = useState(null)
  const [personaMenuMinWidth, setPersonaMenuMinWidth] = useState(undefined)
  const [distanceAnchor, setDistanceAnchor] = useState(null)
  const [priceAnchor, setPriceAnchor] = useState(null)
  const [scheduleAnchor, setScheduleAnchor] = useState(null)
  const [externallyBookableCoaches, setExternallyBookableCoaches] = useState([])
  const [selectedCoachProfile, setSelectedCoachProfile] = useState(null)
  const [coachInfoPanelEnabled] = useFeatureFlags([flags.FLAG_PGA_COACH_INFO_PANEL])
  const [hasClientMounted, setHasClientMounted] = useState(false)
  const [autoExpandNotice, setAutoExpandNotice] = useState(null)
  const [settledSearchKey, setSettledSearchKey] = useState('')
  const debouncedLocationInput = useDebounce(locationInputValue, 250)
  const debouncedPendingMapCenter = useDebounce(pendingMapCenter, MAP_DRAG_SEARCH_DEBOUNCE_MS)
  const selectedPersona = getJourneyPersona(searchState.persona)
  const personaDropdownLabel = selectedPersona?.heroLabel ?? PERSONA_MENU_ALL_RESULTS.label
  const [isDesktop, setIsDesktop] = useState(false)
  const headerShellRef = useRef(null)
  const [navHeight, setNavHeight] = useState(0)
  const cardRefs = useRef({})
  const { toggle: toggleSavedOffering, savedIdSet } = usePgaCoachSavedOfferings()
  const isLoadMoreVisible = useOnScreen(loadMoreSentinelRef)
  const allOfferings = results.allHits || results.hits || []
  const unfilteredHits = results.unfilteredHits || []
  const visibleOfferings = allOfferings
  const coachCardsInterleavedWithList = searchState.savedOfferingsOnly ? [] : externallyBookableCoaches
  const selectedLocation = useMemo(() => parseLatLng(searchState.aroundLatLng), [searchState.aroundLatLng])
  const displayItems = useMemo(() => {
    if (!coachCardsInterleavedWithList.length) {
      return visibleOfferings.map((offering) => ({ type: 'offering', data: offering }))
    }
    const items = []
    let coachIndex = 0
    visibleOfferings.forEach((offering, i) => {
      items.push({ type: 'offering', data: offering })
      if ((i + 1) % 6 === 0 && coachIndex < coachCardsInterleavedWithList.length) {
        items.push({ type: 'coach', data: coachCardsInterleavedWithList[coachIndex % coachCardsInterleavedWithList.length] })
        coachIndex += 1
      }
    })
    if (coachIndex === 0 && coachCardsInterleavedWithList.length && visibleOfferings.length > 0) {
      items.push({ type: 'coach', data: coachCardsInterleavedWithList[0] })
    }
    return items
  }, [visibleOfferings, coachCardsInterleavedWithList])
  const isViewingAwayFromSelectedArea = useMemo(
    () => isViewingOutsideSelectedArea({
      selectedLocation,
      mapCenter: mapViewport?.center,
      radiusMiles: getJourneyDistanceMiles(searchState.distance),
    }),
    [mapViewport, searchState.distance, selectedLocation],
  )
  const hasSearchResults = visibleOfferings.length > 0
  const hasListRows = visibleOfferings.length > 0
  const expandableDistanceOptions = useMemo(
    () => PGA_COACH_DISTANCE_OPTIONS.filter((option) => Number(option.id) > Number(searchState.distance)),
    [searchState.distance],
  )
  const hasExpandableDistanceOptions = expandableDistanceOptions.length > 0
  const filteredIdSet = useMemo(
    () => new Set(allOfferings.map((o) => o.objectID)),
    [allOfferings],
  )
  const fallbackOfferings = useMemo(
    () => computeFallbackOfferings(unfilteredHits, filteredIdSet),
    [unfilteredHits, filteredIdSet],
  )
  const hasFallbackResults = fallbackOfferings.length > 0 && !searchState.savedOfferingsOnly
  const hasOfferingsInAreaIgnoringPersona = Boolean(results.hasOfferingsInAreaIgnoringPersona)
  const emptyStateContent = useMemo(
    () => getCoachSearchEmptyStateCopy({ hasExpandableDistanceOptions, hasFallbackResults }),
    [hasExpandableDistanceOptions, hasFallbackResults],
  )
  const progressContent = useMemo(
    () => getCoachSearchProgressContent(searchState.persona),
    [searchState.persona],
  )
  const helpfulGuidesHref = useMemo(
    () => `${buildJourneySearchUrl({ persona: searchState.persona }, '/coach')}#${PGA_COACH_LANDING_GUIDES_SECTION_ID}`,
    [searchState.persona],
  )
  const personaMenuOptions = useMemo(
    () => [
      ...PGA_COACH_PERSONAS.map((persona) => ({ id: persona.id, label: persona.heroLabel })),
      PERSONA_MENU_ALL_RESULTS,
    ],
    [],
  )

  const journeyDefaults = useMemo(() => getDefaultJourneySearchState(), [])
  const isPersonaFiltered = Boolean(searchState.persona)
  const isDistanceFiltered = searchState.distance !== journeyDefaults.distance
  const isPriceFiltered = searchState.price !== journeyDefaults.price
  const isScheduleFiltered = searchState.schedule !== journeyDefaults.schedule
  const isSavedOfferingsFilterActive = Boolean(searchState.savedOfferingsOnly)
  const hasActiveToolbarRefinements = useMemo(() => (
    searchState.formats.length > 0
    || (searchState.privateCategories?.length ?? 0) > 0
    || searchState.price !== journeyDefaults.price
    || searchState.distance !== journeyDefaults.distance
    || searchState.schedule !== journeyDefaults.schedule
    || searchState.sort !== journeyDefaults.sort
    || Boolean(searchState.savedOfferingsOnly)
  ), [
    journeyDefaults.distance,
    journeyDefaults.price,
    journeyDefaults.schedule,
    journeyDefaults.sort,
    journeyDefaults.savedOfferingsOnly,
    searchState.distance,
    searchState.formats,
    searchState.privateCategories,
    searchState.price,
    searchState.savedOfferingsOnly,
    searchState.schedule,
    searchState.sort,
  ])
  const savedOfferingsFingerprint = useMemo(
    () => (searchState.savedOfferingsOnly ? [...savedIdSet].sort().join('|') : ''),
    [searchState.savedOfferingsOnly, savedIdSet],
  )
  const currentSearchRequestKey = JSON.stringify({
    persona: searchState.persona,
    placeName: searchState.placeName || null,
    aroundLatLng: searchState.aroundLatLng || null,
    query: searchState.query || null,
    formats: [...searchState.formats].sort(),
    privateCategories: [...(searchState.privateCategories || [])].sort(),
    schedule: searchState.schedule,
    price: searchState.price,
    distance: searchState.distance,
    sort: searchState.sort,
    savedOfferingsOnly: searchState.savedOfferingsOnly || false,
    savedOfferingsFingerprint,
    page: currentPage,
  })
  const isSearchSettled = settledSearchKey === currentSearchRequestKey
  const emptyAutoExpandLocationKey = getCoachSearchEmptyAutoExpandLocationKey(searchState)
  const emptyAutoExpandAction = hasClientMounted
    ? getCoachSearchEmptyAutoExpandAction({
      isLoading,
      hasSearchResults,
      savedOfferingsOnly: Boolean(searchState.savedOfferingsOnly),
      isDetectingLocation,
      currentPage,
      errorMessage,
      isMapDragSearch: mapDragGeoInQueryRef.current,
      isSearchSettled,
      searchState,
      journeyDefaults,
      hasOfferingsInAreaIgnoringPersona,
      didAutoExpandDistance: autoExpandDistanceLocationKeyRef.current === emptyAutoExpandLocationKey,
      didAutoShowBest: autoShowBestLocationKeyRef.current === emptyAutoExpandLocationKey,
    })
    : null
  const isAutoExpanding = Boolean(emptyAutoExpandAction)
  const showEmptyRecovery = hasOfferingsInAreaIgnoringPersona
    && !isJourneySearchFullyRelaxed(searchState, journeyDefaults)
  const autoExpandNoticeCopy = getCoachSearchAutoExpandNoticeCopy(autoExpandNotice)

  const setMapAutoFitSuppressed = useCallback((isSuppressed) => {
    suppressMapAutoFitRef.current = isSuppressed
    setSuppressMapAutoFit(isSuppressed)
  }, [])

  const clearPendingMapCenter = useCallback(() => {
    activePendingMapAroundLatLngRef.current = ''
    setPendingMapCenter(null)
  }, [])

  const resetMapDragGeoInQuery = useCallback(() => {
    mapDragGeoInQueryRef.current = false
  }, [])

  const handleMapDragEndCenterChange = useCallback((center) => {
    const nextAroundLatLng = formatAroundLatLng(center)
    if (!nextAroundLatLng) return
    activePendingMapAroundLatLngRef.current = nextAroundLatLng
    setPendingMapCenter(center)
  }, [])

  const setSearchStateWithPageReset = useCallback((nextState, options = {}) => {
    setMapAutoFitSuppressed(Boolean(options.preserveMapViewport))
    setCurrentPage(1)
    setSearchState((previousState) => normalizeJourneySearchState(
      typeof nextState === 'function' ? nextState(previousState) : nextState,
    ))
  }, [setMapAutoFitSuppressed])

  useEffect(() => {
    setHasClientMounted(true)
  }, [])

  useEffect(() => {
    setAutoExpandNotice(null)
  }, [searchState.placeName])

  useEffect(() => {
    const mq = window.matchMedia('(min-width:960px)')
    const update = () => setIsDesktop(mq.matches)
    update()
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update)
      return () => mq.removeEventListener('change', update)
    }
    mq.addListener(update)
    return () => mq.removeListener(update)
  }, [])

  // Lock page scroll while this component is mounted so only the results panel scrolls.
  useEffect(() => {
    const savedHtml = document.documentElement.style.overflow
    const savedBody = document.body.style.overflow
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    return () => {
      document.documentElement.style.overflow = savedHtml
      document.body.style.overflow = savedBody
    }
  }, [])

  // Measure the actual sticky nav height by summing all visible AppBar elements.
  // display:none elements (the hidden desktop/mobile header) contribute offsetHeight=0,
  // so GlobalEventAlertQuery (and its LinearProgress) are included automatically.
  useEffect(() => {
    const appBars = Array.from(document.querySelectorAll('header.MuiAppBar-positionSticky'))
    if (!appBars.length) return undefined

    const measure = () => {
      const total = appBars.reduce((sum, el) => sum + el.offsetHeight, 0)
      if (total > 0) setNavHeight(total)
    }

    measure()

    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(measure)
    appBars.forEach((el) => ro.observe(el))
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    if (hasHydratedSearchStorageRef.current) return
    hasHydratedSearchStorageRef.current = true
    const lastSearchState = loadJourneyLastSearchState()
    const persistedSavedSearches = loadJourneySavedSearches()
    setSavedSearches(persistedSavedSearches)
    if (!lastSearchState) return
    if (hasUrlSpecifiedJourneySearchState(initialSearchState)) return
    setSearchStateWithPageReset((previousState) => ({
      ...previousState,
      ...lastSearchState,
      // Keep URL persona (including unset All results); do not restore Beginner from last search.
      persona: previousState.persona,
    }))
    setLocationInputValue(lastSearchState.placeName || '')
  }, [initialSearchState, setSearchStateWithPageReset])

  useEffect(() => {
    if (skipLastSearchPersistRef.current) {
      skipLastSearchPersistRef.current = false
      return
    }
    saveJourneyLastSearchState(searchState)
  }, [searchState])

  useEffect(() => {
    let ignore = false
    const loadLocation = async () => {
      if (searchState.placeName || searchState.aroundLatLng) {
        setIsDetectingLocation(false)
        return
      }
      try {
        const location = await detectJourneyLocationFromIp()
        if (!ignore && location) {
          setSearchStateWithPageReset((previousState) => ({
            ...previousState,
            ...location,
          }))
          setLocationInputValue(location.placeName)
        }
      } catch (error) {
        // Search still works without a hydrated location.
      } finally {
        if (!ignore) setIsDetectingLocation(false)
      }
    }
    loadLocation()
    return () => {
      ignore = true
    }
  }, [searchState.aroundLatLng, searchState.placeName, setSearchStateWithPageReset])

  useEffect(() => {
    let ignore = false
    const loadLocations = async () => {
      if (!debouncedLocationInput || debouncedLocationInput === searchState.placeName) {
        if (!ignore) setLocationOptions([])
        return
      }
      const hits = await autocompleteCity(debouncedLocationInput)
      if (ignore) return
      setLocationOptions(hits.map(mapAlgoliaCityHitToLocationOption))
    }
    loadLocations()
    return () => {
      ignore = true
    }
  }, [debouncedLocationInput, searchState.placeName])

  useEffect(() => {
    let ignore = false
    const loadResults = async () => {
      if (currentPage === 1) {
        setIsLoading(true)
      } else {
        setIsLoadingMore(true)
      }
      try {
        const nextResults = await searchPgaCoachOfferings({
          ...searchState,
          page: currentPage,
          perPage: JOURNEY_OFFERINGS_PAGE_SIZE,
          ...(searchState.savedOfferingsOnly
            ? { savedOfferingIds: Array.from(savedIdSet) }
            : {}),
        })
        if (ignore) return
        setSettledSearchKey(currentSearchRequestKey)
        if (currentPage === 1) {
          setExternallyBookableCoaches(nextResults.coachHits || [])
        }
        setResults((previousResults) => {
          if (currentPage === 1) return nextResults
          const previousHits = previousResults.allHits || previousResults.hits || []
          const nextHits = nextResults.allHits || nextResults.hits || []
          const existingIds = new Set(previousHits.map((offering) => String(offering.objectID)))
          const appendedHits = nextHits.filter((offering) => !existingIds.has(String(offering.objectID)))
          const mergedHits = previousHits.concat(appendedHits)
          return {
            ...nextResults,
            hits: mergedHits,
            allHits: mergedHits,
            totalHits: mergedHits.length,
            totalPages: Math.max(Math.ceil(mergedHits.length / (nextResults.perPage || 1)), 1),
          }
        })
        setErrorMessage('')
        const viewResultsEventKey = JSON.stringify({
          personaId: searchState.persona,
          placeName: searchState.placeName || null,
          query: searchState.query || null,
          formats: [...searchState.formats].sort(),
          schedule: searchState.schedule,
          price: searchState.price,
          distance: searchState.distance,
          sort: searchState.sort,
          savedOfferingsOnly: searchState.savedOfferingsOnly || false,
          savedOfferingsFingerprint: searchState.savedOfferingsOnly ? savedOfferingsFingerprint : '',
        })
        if (currentPage === 1 && viewResultsEventKey !== lastViewResultsEventRef.current) {
          lastViewResultsEventRef.current = viewResultsEventKey
          amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.viewResults, {
            personaId: searchState.persona,
            placeName: searchState.placeName || null,
            query: searchState.query || null,
            totalHits: nextResults.totalHits,
            formats: searchState.formats,
            schedule: searchState.schedule,
            price: searchState.price,
            distance: searchState.distance,
            sort: searchState.sort,
            saved_offerings_only: Boolean(searchState.savedOfferingsOnly),
          })
        }
      } catch (error) {
        if (!ignore) {
          setSettledSearchKey(currentSearchRequestKey)
          setResults(EMPTY_RESULTS)
          setErrorMessage(error.message)
        }
      } finally {
        if (!ignore) {
          setIsLoading(false)
          setIsLoadingMore(false)
        }
      }
    }
    loadResults()
    return () => {
      ignore = true
    }
  }, [currentPage, currentSearchRequestKey, searchState, savedOfferingsFingerprint])

  useEffect(() => {
    const nextAroundLatLng = formatAroundLatLng(debouncedPendingMapCenter)
    if (!nextAroundLatLng) return
    if (activePendingMapAroundLatLngRef.current !== nextAroundLatLng) return
    clearPendingMapCenter()
    const currentAroundLatLng = formatAroundLatLng(parseLatLng(searchState.aroundLatLng))
    if (currentAroundLatLng === nextAroundLatLng) return
    mapDragGeoInQueryRef.current = true
    setAutoExpandNotice(null)
    setSearchStateWithPageReset((previousState) => {
      return {
        ...previousState,
        aroundLatLng: nextAroundLatLng,
      }
    }, { preserveMapViewport: true })
  }, [clearPendingMapCenter, debouncedPendingMapCenter, searchState.aroundLatLng, setSearchStateWithPageReset])

  useEffect(() => {
    const currentUrl = router.asPath ? router.asPath.split('#')[0] : ''
    const currentPathname = currentUrl.split('?')[0]
    const shouldPreserveSeoPath = currentPathname.startsWith(`${PGA_COACH_PROGRAMS_SEARCH_PATHNAME}/`)
    const clearedPersonaFromPersonaSeoPath = Boolean(pathnameEmbedsPersona(currentPathname))
      && !searchState.persona
    const geoQueryInCurrentUrl = journeyUrlHasGeoQueryParams(currentUrl)
    const includeAroundLatLngInQuery = geoQueryInCurrentUrl.aroundLatLng
      || mapDragGeoInQueryRef.current
      || explicitLocationInQueryRef.current
    const includePlaceNameInQuery = geoQueryInCurrentUrl.placeName
      || explicitLocationInQueryRef.current
    const isUnscopedProgramsPath = isUnscopedProgramsSearchPath(currentPathname)
    if (autoShowBestKeepCitySeoPathRef.current && (
      searchState.persona || !pathnameEmbedsPersona(currentPathname)
    )) {
      autoShowBestKeepCitySeoPathRef.current = false
    }
    const keepCityPathAfterAutoShowBest = autoShowBestKeepCitySeoPathRef.current
      && clearedPersonaFromPersonaSeoPath
    const urlSearchState = isUnscopedProgramsPath
      ? {
        ...searchState,
        placeName: includePlaceNameInQuery ? searchState.placeName : '',
        aroundLatLng: includeAroundLatLngInQuery ? searchState.aroundLatLng : '',
      }
      : searchState
    let nextUrl = buildJourneySearchUrl(urlSearchState)
    if ((keepCityPathAfterAutoShowBest || !clearedPersonaFromPersonaSeoPath) && shouldPreserveSeoPath) {
      const seoPathname = explicitLocationInQueryRef.current
        ? buildIndexableCoachSearchPathname(searchState)
        : (buildProgramsSeoPathnameFromCurrent(currentPathname, { persona: searchState.persona })
          || currentPathname)
      nextUrl = buildJourneySearchUrl(
        searchState,
        seoPathname,
        { includePlaceNameInQuery, includeAroundLatLngInQuery },
      )
    }
    if (currentUrl === nextUrl) return
    router.replace(nextUrl, undefined, { shallow: true })
  }, [router, router.asPath, searchState])

  useEffect(() => {
    if (!isLoadMoreVisible) {
      canLoadMoreOnVisibleRef.current = true
      return
    }
    if (isLoading || isLoadingMore) return
    if (!isLoadMoreVisible || !results.hasMore) return
    if (!canLoadMoreOnVisibleRef.current) return
    canLoadMoreOnVisibleRef.current = false
    setIsLoadingMore(true)
    setCurrentPage((page) => page + 1)
  }, [isLoadMoreVisible, isLoading, isLoadingMore, results.hasMore])

  useEffect(() => {
    setHoveredOfferingId(null)
    setSelectedOfferingId(null)
    setMapPinPopupAnchorOfferingId(null)
    setMapOpenDetailsSignal(null)
    if (suppressMapAutoFitRef.current) return
    setMapViewport(null)
    setMapResetKey((value) => value + 1)
  }, [searchState])

  useEffect(() => {
    if (isDesktop && !showDesktopMap) {
      setMapViewport(null)
    }
  }, [isDesktop, showDesktopMap])

  useEffect(() => {
    if (!selectedOfferingId) return
    const selectedOfferingStillExists = visibleOfferings.some((offering) => offering.objectID === selectedOfferingId)
    if (!selectedOfferingStillExists) {
      setSelectedOfferingId(null)
      setMapPinPopupAnchorOfferingId(null)
      setMapOpenDetailsSignal(null)
    }
  }, [visibleOfferings, selectedOfferingId])

  const handleCoachProfileClose = useCallback(() => {
    setSelectedCoachProfile(null)
  }, [])

  const handleCoachProfileOpen = useCallback((offering) => {
    const locationLabel = offering.venueName
      || [offering.city, offering.state].filter(Boolean).join(', ')
      || null
    setSelectedCoachProfile({
      coach: offering.coach,
      profileUrl: offering.profileUrl,
      locationLabel,
    })
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.coachInfoPanelOpen, {
      source: 'list',
      coach_name: offering.coach?.name || null,
      coach_slug: offering.coach?.slug || null,
    })
  }, [])

  const handleCoachProfileCtaClick = useCallback(() => {
    if (!selectedCoachProfile) return
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.coachInfoPanelCtaClick, {
      source: 'list',
      coach_name: selectedCoachProfile.coach?.name || null,
      coach_slug: selectedCoachProfile.coach?.slug || null,
    })
  }, [selectedCoachProfile])

  useEffect(() => {
    if (!selectedCoachProfile) return
    setSelectedCoachProfile(null)
  }, [visibleOfferings, searchState.aroundLatLng, searchState.persona])

  const handleOfferingSelect = (selectedOffering) => {
    setSelectedOfferingId(selectedOffering.objectID)
    setMapPinPopupAnchorOfferingId(null)
    setMapOpenDetailsSignal(null)
  }

  const handleMapOfferingSelect = (offeringId, meta = {}) => {
    if (offeringId == null) {
      setSelectedOfferingId(null)
      setMapPinPopupAnchorOfferingId(null)
      setMapOpenDetailsSignal(null)
      return
    }
    setSelectedOfferingId(offeringId)
    if (meta.source === 'marker' && offeringId) {
      const pinOffering = visibleOfferings.find((offering) => offering.objectID === offeringId)
      const offeringsAtPin = Array.isArray(meta.offeringIds) ? meta.offeringIds.length : 0
      amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.clickMapPin, {
        facility_id: getAnalyticsFacilityIdFromOffering(pinOffering),
        facility_name: pinOffering?.venueName ?? null,
        offerings_at_pin: offeringsAtPin,
        persona: searchState.persona,
      })
      setMapPinPopupAnchorOfferingId(offeringId)
      // Keep map visible so map popups/sheets can render.
      setMapOpenDetailsSignal(null)
      return
    }
    if (meta.source === 'popup-item' && offeringId) {
      const popupOffering = visibleOfferings.find((offering) => offering.objectID === offeringId)
      if (popupOffering) {
        amplitude.track(
          PGA_COACH_JOURNEY_ANALYTICS.offeringClick,
          buildPgaCoachOfferingClickAnalyticsPayload(popupOffering, 'map', meta.position, searchState.persona),
        )
      }
      mapOpenDetailsNonceRef.current += 1
      setMapOpenDetailsSignal({
        offeringId,
        nonce: mapOpenDetailsNonceRef.current,
      })
      return
    }
    setMapOpenDetailsSignal(null)
    setMapPinPopupAnchorOfferingId(null)
  }

  useEffect(() => {
    if (!selectedOfferingId || !isDesktop) return
    const cardNode = cardRefs.current[selectedOfferingId]
    if (cardNode) {
      cardNode.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [isDesktop, selectedOfferingId])

  const resultsHeadline = formatResultsHeadline(selectedPersona, searchState.placeName)
  const locationLabel = searchState.placeName || 'you'
  const resultCountForSubtitle = results.totalHits
  const subtitleLocation = isViewingAwayFromSelectedArea ? 'this area' : locationLabel
  const resultsSubtitle = (() => {
    if (isLoading) return 'Searching offerings...'
    if (searchState.savedOfferingsOnly) {
      return results.totalHits === 1
        ? '1 saved offering'
        : `${results.totalHits} saved offerings`
    }
    const resultWord = resultCountForSubtitle === 1 ? 'result' : 'results'
    return `${resultCountForSubtitle} ${resultWord} near ${subtitleLocation}`
  })()

  const {
    title: pageTitle,
    description: pageDescription,
    canonicalUrl,
    breadcrumbList,
    unscopedDocument,
    shareImageUrl,
  } = getGolfLessonsDocumentSeo({
    personaId: searchState.persona,
    placeName: searchState.placeName,
    searchState,
    asPath: router.asPath,
    baseUrl: defaults.baseUrl,
  })
  const seoNoIndex = shouldNoIndexGolfLessonsDocument({
    asPath: router.asPath,
    query: searchState.query,
    isLoading,
    totalHits: results.totalHits,
  })

  const applyLocationOption = (option) => {
    if (!option?.placeName || !option?.aroundLatLng) return
    clearPendingMapCenter()
    resetMapDragGeoInQuery()
    explicitLocationInQueryRef.current = true
    setSearchStateWithPageReset((previousState) => ({
      ...previousState,
      placeName: option.placeName,
      aroundLatLng: option.aroundLatLng,
    }))
    setLocationInputValue(option.placeName)
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.locationChange, {
      from_location: searchState.placeName,
      to_location: option.placeName,
    })
  }

  const handleLocationSelect = (option) => {
    if (option == null) return
    if (typeof option !== 'string') {
      applyLocationOption(option)
      return
    }
    const typed = option.trim()
    if (!typed) return
    setLocationInputValue(typed)
    resolveLocationOptionFromInput(typed, locationOptionsRef.current)
      .then((resolved) => {
        if (resolved) applyLocationOption(resolved)
      })
      .catch(() => {})
  }

  const serializeFilterValue = (value) => {
    if (Array.isArray(value)) return value.length ? [...value].sort().join(',') : null
    return value || null
  }

  const trackFilterChange = ({ filterName, fromValue, toValue }) => {
    if (fromValue === toValue) return
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.filterChange, {
      filter_name: filterName,
      flter_value_from: fromValue,
      filter_value_to: toValue,
      persona: searchState.persona,
    })
  }

  const toggleFormat = (formatId) => {
    const previousFormats = searchState.formats
    const formats = previousFormats.includes(formatId)
      ? previousFormats.filter((format) => format !== formatId)
      : previousFormats.concat(formatId)
    trackFilterChange({
      filterName: 'format',
      fromValue: serializeFilterValue(previousFormats),
      toValue: serializeFilterValue(formats),
    })
    setSearchStateWithPageReset((previousState) => ({
      ...previousState,
      formats,
    }))
  }

  const togglePrivateFormat = () => {
    const previousFormats = searchState.formats
    const isSelected = previousFormats.includes('private')
    const nextFormats = isSelected
      ? previousFormats.filter((f) => f !== 'private')
      : [...previousFormats, 'private']
    trackFilterChange({
      filterName: 'format',
      fromValue: serializeFilterValue(previousFormats),
      toValue: serializeFilterValue(nextFormats),
    })
    setSearchStateWithPageReset((previousState) => ({
      ...previousState,
      formats: nextFormats,
      privateCategories: [],
    }))
  }

  const handlePrivateCategoryChange = (nextCategories) => {
    setSearchStateWithPageReset((previousState) => {
      const willHaveCategories = nextCategories.length > 0
      const isPrivateSelected = previousState.formats.includes('private')
      const previousFormats = previousState.formats
      const previousPrivateCategories = previousState.privateCategories
      let { formats } = previousState
      if (willHaveCategories && !isPrivateSelected) {
        formats = [...previousState.formats, 'private']
      } else if (!willHaveCategories && isPrivateSelected && previousState.privateCategories.length > 0) {
        formats = previousState.formats.filter((f) => f !== 'private')
      }
      trackFilterChange({
        filterName: 'private_categories',
        fromValue: serializeFilterValue(previousPrivateCategories),
        toValue: serializeFilterValue(nextCategories),
      })
      trackFilterChange({
        filterName: 'format',
        fromValue: serializeFilterValue(previousFormats),
        toValue: serializeFilterValue(formats),
      })
      return {
        ...previousState,
        formats,
        privateCategories: nextCategories,
      }
    })
  }

  const saveCurrentSearch = () => {
    const nextSavedSearches = saveJourneySavedSearch(searchState)
    setSavedSearches(nextSavedSearches)
    setSaveMessage('Search saved for your next visit.')
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.saveSearch, {
      personaId: searchState.persona,
      placeName: searchState.placeName || null,
    })
    window.setTimeout(() => setSaveMessage(''), 3000)
  }

  const updateSearchState = (nextState) => {
    setSearchStateWithPageReset(nextState)
  }

  const applyFullRefinementReset = useCallback(() => {
    setSearchStateWithPageReset((previousState) => ({
      ...previousState,
      formats: [],
      privateCategories: [],
      price: journeyDefaults.price,
      schedule: journeyDefaults.schedule,
      distance: journeyDefaults.distance,
      sort: journeyDefaults.sort,
      savedOfferingsOnly: journeyDefaults.savedOfferingsOnly,
    }))
  }, [journeyDefaults, setSearchStateWithPageReset])

  const applyShowBestRecovery = useCallback(() => {
    setSearchStateWithPageReset((previousState) => ({
      ...previousState,
      persona: '',
      query: '',
      formats: [],
      privateCategories: [],
      price: journeyDefaults.price,
      schedule: journeyDefaults.schedule,
      distance: journeyDefaults.distance,
      sort: journeyDefaults.sort,
      savedOfferingsOnly: journeyDefaults.savedOfferingsOnly,
    }))
  }, [journeyDefaults, setSearchStateWithPageReset])

  useEffect(() => {
    if (!emptyAutoExpandAction) {
      if (
        hasClientMounted
        && !isLoading
        && isSearchSettled
        && currentPage === 1
        && !errorMessage
        && searchState.placeName
        && !isDetectingLocation
      ) {
        autoExpandDistanceLocationKeyRef.current = emptyAutoExpandLocationKey
        autoShowBestLocationKeyRef.current = emptyAutoExpandLocationKey
      }
      return
    }
    setIsLoading(true)
    skipLastSearchPersistRef.current = true
    if (emptyAutoExpandAction.type === 'expand_distance') {
      autoExpandDistanceLocationKeyRef.current = emptyAutoExpandLocationKey
      amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.filterChange, {
        filter_name: 'empty_state_auto_expand',
        flter_value_from: emptyAutoExpandAction.fromDistance,
        filter_value_to: emptyAutoExpandAction.toDistance,
        persona: searchState.persona,
      })
      setAutoExpandNotice({
        type: 'expand_distance',
        fromMiles: getJourneyDistanceMiles(emptyAutoExpandAction.fromDistance),
        toMiles: getJourneyDistanceMiles(emptyAutoExpandAction.toDistance),
      })
      setSearchStateWithPageReset((previousState) => ({
        ...previousState,
        distance: emptyAutoExpandAction.toDistance,
      }))
      return
    }
    autoShowBestLocationKeyRef.current = emptyAutoExpandLocationKey
    autoShowBestKeepCitySeoPathRef.current = true
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.filterChange, {
      filter_name: 'empty_state_auto_expand',
      flter_value_from: 'active',
      filter_value_to: 'persona_and_refinements_cleared',
      persona: searchState.persona,
    })
    setAutoExpandNotice({ type: 'show_best' })
    applyShowBestRecovery()
  }, [
    applyShowBestRecovery,
    currentPage,
    emptyAutoExpandAction,
    emptyAutoExpandLocationKey,
    errorMessage,
    hasClientMounted,
    isDetectingLocation,
    isLoading,
    isSearchSettled,
    searchState.persona,
    searchState.placeName,
    setSearchStateWithPageReset,
  ])

  const handleOfferingFavoriteToggle = useCallback((offeringId) => {
    const saved = toggleSavedOffering(offeringId)
    if (searchState.savedOfferingsOnly) setCurrentPage(1)
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.offeringFavoriteToggle, {
      offering_id: offeringId,
      saved,
      persona: searchState.persona,
    })
  }, [toggleSavedOffering, searchState.persona, searchState.savedOfferingsOnly])

  const toggleSavedOfferingsOnlyFilter = () => {
    const next = !searchState.savedOfferingsOnly
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.savedOfferingsFilterToggle, {
      enabled: next,
      persona: searchState.persona,
    })
    trackFilterChange({
      filterName: 'saved_offerings_only',
      fromValue: searchState.savedOfferingsOnly ? '1' : '0',
      toValue: next ? '1' : '0',
    })
    updateSearchState((previousState) => ({
      ...previousState,
      savedOfferingsOnly: next,
    }))
  }

  /** Empty-state secondary: reveal filter toolbar on mobile and bring it into view. */
  const openFiltersForAdjustment = useCallback(() => {
    if (!isDesktop) {
      setShowMobileFilters(true)
    }
    const scrollToToolbar = () => {
      document.getElementById('coach-search-filter-toolbar')?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
    if (!isDesktop) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(scrollToToolbar)
      })
    } else {
      scrollToToolbar()
    }
  }, [isDesktop])

  /** Empty-state primary: widest inventory at the current location (clears persona + query + toolbar refinements). */
  const relaxFilters = () => {
    trackFilterChange({
      filterName: 'empty_state_show_best',
      fromValue: 'active',
      toValue: 'persona_and_refinements_cleared',
    })
    applyShowBestRecovery()
  }

  const clearAllToolbarFilters = () => {
    trackFilterChange({
      filterName: 'clear_all',
      fromValue: 'active',
      toValue: 'defaults',
    })
    applyFullRefinementReset()
  }

  const renderEmptyStateIcon = () => {
    const EmptyStateIcon = selectedPersona?.icon ?? ExploreOutlinedIcon
    return (
      <Box className={classes.emptyIconShell} aria-hidden="true">
        <EmptyStateIcon className={classes.emptyIcon} fontSize="large" />
      </Box>
    )
  }

  const renderEmptyStateDivider = () => (
    <Box className={classes.emptyDivider} aria-hidden="true">
      <span className={classes.emptyDividerLine} />
      <span className={classes.emptyDividerBadge}>OR</span>
      <span className={classes.emptyDividerLine} />
    </Box>
  )

  const renderEmptyStateGuides = () => (
    <Box pt={4} className={classes.emptyContentBlock}>
      {/*
        Same 88px gold-tint disc + gold glyph as the recovery badge above
        (`emptyIconShell` / `emptyIcon`). `MenuBookOutlined` reads as
        "guides / learning material" which matches the section copy + CTA.
      */}
      <Box className={classes.emptyIconShell} aria-hidden="true">
        <MenuBookOutlinedIcon className={classes.emptyIcon} fontSize="large" />
      </Box>
      <Typography component="p" variant="body2" className={classes.emptyContentHeading}>
        {progressContent.heading}
      </Typography>
      <Typography component="h3" variant="h5" className={classes.emptyContentTitle}>
        {progressContent.title}
      </Typography>
      <Typography className={`${classes.meta} ${classes.emptyContentDescription}`}>
        {progressContent.description}
      </Typography>
      <Box pt={2} className={classes.emptyActions}>
        <Button
          variant="outlined"
          color="primary"
          className={classes.emptyActionButton}
          href={helpfulGuidesHref}
        >
          {progressContent.ctaLabel}
        </Button>
      </Box>
    </Box>
  )

  const mapSummaryLabel = resultsSubtitle

  const resultJsonLd = useMemo(() => {
    if (unscopedDocument) {
      return buildGolfLessonsLandingJsonLd({
        title: pageTitle,
        description: pageDescription,
        canonicalUrl,
        image: shareImageUrl,
        baseUrl: defaults.baseUrl,
      })
    }
    return buildGolfLessonsResultJsonLd(visibleOfferings, defaults.baseUrl)
  }, [
    unscopedDocument,
    pageTitle,
    pageDescription,
    canonicalUrl,
    shareImageUrl,
    visibleOfferings,
  ])

  const desktopSplitMap = isDesktop && showDesktopMap
  const isMapViewSelected = isDesktop ? showDesktopMap : mobileView === 'map'
  let resultsColumnFlex
  if (isDesktop) {
    resultsColumnFlex = desktopSplitMap ? '0 0 50%' : '1'
  }
  const savedSearchCount = savedSearches.length

  const toggleLayoutView = () => {
    if (isDesktop) {
      const nextShowsMap = !showDesktopMap
      amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.toggleMapView, {
        view: nextShowsMap ? 'map' : 'list',
        persona: searchState.persona,
      })
      setShowDesktopMap(nextShowsMap)
      return
    }
    const nextMobileView = mobileView === 'map' ? 'results' : 'map'
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.toggleMapView, {
      view: nextMobileView === 'map' ? 'map' : 'list',
      persona: searchState.persona,
    })
    setMobileView(nextMobileView)
  }

  const applySavedSearch = (savedSearchState) => {
    if (!savedSearchState) return
    clearPendingMapCenter()
    resetMapDragGeoInQuery()
    explicitLocationInQueryRef.current = true
    updateSearchState(savedSearchState)
    setLocationInputValue(savedSearchState.placeName || '')
    setSavedSearchAnchor(null)
  }

  const removeSavedSearch = (savedSearchState) => {
    if (!savedSearchState) return
    const nextSavedSearches = removeJourneySavedSearch(savedSearchState)
    setSavedSearches(nextSavedSearches)
  }

  return (
    <div
      className={classes.root}
      style={navHeight > 0 ? { height: `calc(100vh - ${navHeight}px)` } : { height: '100vh' }}
    >
      <NextSeo
        noindex={defaults.noindexNofollow || seoNoIndex}
        nofollow={defaults.noindexNofollow || seoNoIndex}
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        openGraph={{
          title: pageTitle,
          description: pageDescription,
          url: canonicalUrl,
          type: 'website',
          site_name: 'PGA of America',
          images: shareImageUrl ? [{
            url: shareImageUrl,
            width: 1200,
            alt: 'PGA Coach golf lessons',
          }] : undefined,
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />
      <Container maxWidth="xl" className={classes.outerContainer}>
        <JsonLd {...breadcrumbList} />
        <JsonLd {...resultJsonLd} />
        <div
          className={classes.headerShell}
          ref={headerShellRef}
        >
          <div className={classes.searchHeaderPanel}>
            <div className={classes.contextSection}>
              <div className={classes.contextFields}>
                <div className={classes.contextField}>
                  <Typography component="span" variant="caption" className={classes.contextFieldCaption}>
                    You&apos;re looking for:
                  </Typography>
                  <Button
                    type="button"
                    disableElevation
                    className={[
                      classes.contextDropdownTrigger,
                      Boolean(personaAnchor) && classes.contextDropdownTriggerOpen,
                      isPersonaFiltered && classes.contextControlActive,
                    ].filter(Boolean).join(' ')}
                    aria-haspopup="true"
                    aria-expanded={Boolean(personaAnchor)}
                    onClick={(event) => {
                      setPersonaMenuMinWidth(event.currentTarget.offsetWidth)
                      setPersonaAnchor(event.currentTarget)
                    }}
                  >
                    <span className={classes.contextDropdownValue}>{personaDropdownLabel}</span>
                    <ChevronDownIcon
                      {...COACH_PROFILE_FILTER_ICON_PROPS}
                      size={18}
                      className={[
                        classes.contextDropdownCaret,
                        personaAnchor && classes.contextDropdownCaretOpen,
                      ].filter(Boolean).join(' ')}
                    />
                  </Button>
                  <CoachFilterMenu
                    classes={classes}
                    anchorEl={personaAnchor}
                    onClose={() => setPersonaAnchor(null)}
                    options={personaMenuOptions}
                    selectedId={searchState.persona}
                    onSelectId={(personaId) => {
                      updateSearchState((previousState) => ({ ...previousState, persona: personaId }))
                      amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.personaTabClick, {
                        place_name: searchState.placeName || null,
                        from_persona: searchState.persona,
                        to_persona: personaId,
                      })
                    }}
                    paperClassName={classes.contextMenuPaperAttached}
                    paperStyle={personaMenuMinWidth != null ? { minWidth: personaMenuMinWidth } : undefined}
                    dividerAfterIndices={[PGA_COACH_PERSONAS.length - 1]}
                  />
                </div>
                <div className={classes.contextField}>
                  <Typography component="span" variant="caption" className={classes.contextFieldCaption}>
                    Near:
                  </Typography>
                  <Autocomplete
                    autoHighlight
                    freeSolo
                    className={classes.contextNearAutocomplete}
                    classes={{ paper: classes.contextAutocompletePaper }}
                    filterOptions={(options) => options}
                    options={locationOptions}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                    inputValue={locationInputValue}
                    onInputChange={(event, value, reason) => {
                      if (reason === 'reset') return
                      setLocationInputValue(value)
                    }}
                    onChange={(event, option) => handleLocationSelect(option)}
                    popupIcon={(
                      <ChevronDownIcon
                        {...COACH_PROFILE_FILTER_ICON_PROPS}
                        size={18}
                        className={classes.contextDropdownCaret}
                      />
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="City, State"
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <MapPinIcon
                                  {...COACH_PROFILE_FILTER_ICON_PROPS}
                                  size={18}
                                  className={classes.contextLocationIcon}
                                />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {isDetectingLocation ? <CircularProgress size={18} color="secondary" /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <Divider className={classes.sectionDivider} />

            <div className={classes.filterSection}>
              {!isDesktop && (
                <div className={classes.mobileControlsRow}>
                  <Button
                    type="button"
                    variant="text"
                    className={[
                      classes.mobileFilterToggleButton,
                      hasActiveToolbarRefinements && classes.mobileFilterToggleButtonActive,
                    ].filter(Boolean).join(' ')}
                    startIcon={(
                      <SlidersHorizontalIcon
                        {...COACH_PROFILE_FILTER_ICON_PROPS}
                        size={16}
                      />
                    )}
                    endIcon={showMobileFilters
                      ? (
                        <ChevronUpIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )
                      : (
                        <ChevronDownIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                    onClick={() => setShowMobileFilters((prev) => !prev)}
                    aria-expanded={showMobileFilters}
                    aria-controls="coach-search-filter-toolbar"
                  >
                    {`Filters${hasActiveToolbarRefinements ? ' •' : ''}`}
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    className={classes.layoutViewToggleButton}
                    startIcon={isMapViewSelected
                      ? (
                        <MapIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )
                      : (
                        <ListIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                    onClick={toggleLayoutView}
                    aria-label={isMapViewSelected
                      ? 'Map view selected. Switch to list view without map'
                      : 'List view selected. Switch to map view with list and map'}
                  >
                    {isMapViewSelected ? 'Map' : 'List'}
                  </Button>
                </div>
              )}
              {(isDesktop || showMobileFilters) && (
                <div id="coach-search-filter-toolbar" className={classes.filterToolbar}>
                  <div className={classes.formatChips}>
                    <PrivateFormatChip
                      classes={classes}
                      isPrivateSelected={searchState.formats.includes('private')}
                      privateCategories={searchState.privateCategories || []}
                      onToggleAll={togglePrivateFormat}
                      onCategoryChange={handlePrivateCategoryChange}
                    />
                    {PGA_COACH_FORMAT_OPTIONS.filter((f) => f.id !== 'private').map((format) => {
                      const FormatIcon = FORMAT_CHIP_ICONS[format.id]
                      return (
                        <Chip
                          key={format.id}
                          icon={FormatIcon ? (
                            <FormatIcon
                              {...COACH_PROFILE_FILTER_ICON_PROPS}
                              size={16}
                            />
                          ) : undefined}
                          label={format.label}
                          onClick={() => toggleFormat(format.id)}
                          className={[
                            classes.formatChip,
                            searchState.formats.includes(format.id) && classes.formatChipSelected,
                          ].filter(Boolean).join(' ')}
                        />
                      )
                    })}
                  </div>
                  <div className={classes.toolbarDivider} />
                  <div className={classes.iconFilterGroup}>
                    <Button
                      type="button"
                      variant="text"
                      aria-label={`Search radius, ${PGA_COACH_DISTANCE_OPTIONS.find((o) => o.id === searchState.distance)?.label || 'distance'} selected`}
                      className={[
                        classes.filterPill,
                        classes.filterPillIconOnly,
                        isDistanceFiltered && classes.toggleButtonActive,
                      ].filter(Boolean).join(' ')}
                      onClick={(e) => setDistanceAnchor(e.currentTarget)}
                      startIcon={(
                        <MapPinIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                      endIcon={(
                        <ChevronDownIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                    />
                    <CoachFilterMenu
                      classes={classes}
                      anchorEl={distanceAnchor}
                      onClose={() => setDistanceAnchor(null)}
                      options={PGA_COACH_DISTANCE_OPTIONS}
                      selectedId={searchState.distance}
                      onSelectId={(id) => {
                        trackFilterChange({
                          filterName: 'distance',
                          fromValue: searchState.distance,
                          toValue: id,
                        })
                        setAutoExpandNotice(null)
                        updateSearchState((previousState) => ({ ...previousState, distance: id }))
                      }}
                    />

                    <Button
                      type="button"
                      variant="text"
                      aria-label={`Price, ${PGA_COACH_PRICE_OPTIONS.find((o) => o.id === searchState.price)?.label || 'price'} selected`}
                      className={[
                        classes.filterPill,
                        classes.filterPillIconOnly,
                        isPriceFiltered && classes.toggleButtonActive,
                      ].filter(Boolean).join(' ')}
                      onClick={(e) => setPriceAnchor(e.currentTarget)}
                      startIcon={(
                        <PriceIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                      endIcon={(
                        <ChevronDownIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                    />
                    <CoachFilterMenu
                      classes={classes}
                      anchorEl={priceAnchor}
                      onClose={() => setPriceAnchor(null)}
                      options={PGA_COACH_PRICE_OPTIONS}
                      selectedId={searchState.price}
                      onSelectId={(id) => {
                        trackFilterChange({
                          filterName: 'price',
                          fromValue: searchState.price,
                          toValue: id,
                        })
                        updateSearchState((previousState) => ({ ...previousState, price: id }))
                      }}
                    />

                    <Button
                      type="button"
                      variant="text"
                      aria-label={`Availability window, ${PGA_COACH_SCHEDULE_OPTIONS.find((o) => o.id === searchState.schedule)?.label || 'any time'} selected`}
                      className={[
                        classes.filterPill,
                        classes.filterPillIconOnly,
                        isScheduleFiltered && classes.toggleButtonActive,
                      ].filter(Boolean).join(' ')}
                      onClick={(e) => setScheduleAnchor(e.currentTarget)}
                      startIcon={(
                        <CalendarIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                      endIcon={(
                        <ChevronDownIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                    />
                    <CoachFilterMenu
                      classes={classes}
                      anchorEl={scheduleAnchor}
                      onClose={() => setScheduleAnchor(null)}
                      options={PGA_COACH_SCHEDULE_OPTIONS}
                      selectedId={searchState.schedule}
                      onSelectId={(id) => {
                        trackFilterChange({
                          filterName: 'schedule',
                          fromValue: searchState.schedule,
                          toValue: id,
                        })
                        updateSearchState((previousState) => ({ ...previousState, schedule: id }))
                      }}
                    />
                  </div>

                  <div className={classes.toolbarDivider} role="presentation" />

                  <Tooltip
                    title={
                      searchState.savedOfferingsOnly
                        ? 'Show all offerings'
                        : 'Show only offerings you saved on this device'
                    }
                  >
                    <Button
                      type="button"
                      variant="text"
                      aria-label={
                        searchState.savedOfferingsOnly
                          ? 'Saved offerings filter is on. Turn off to show all results.'
                          : 'Show only saved offerings'
                      }
                      aria-pressed={isSavedOfferingsFilterActive}
                      onClick={toggleSavedOfferingsOnlyFilter}
                      startIcon={(
                        <HeartIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                          fill={isSavedOfferingsFilterActive ? 'currentColor' : 'none'}
                        />
                      )}
                      className={[
                        classes.savedOfferingsFilterCircle,
                        isSavedOfferingsFilterActive && classes.savedOfferingsFilterCircleActive,
                      ].filter(Boolean).join(' ')}
                    />
                  </Tooltip>

                  {hasActiveToolbarRefinements ? (
                    <Button
                      type="button"
                      variant="text"
                      className={classes.saveButton}
                      aria-label="Clear all filters"
                      startIcon={(
                        <XIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                      onClick={clearAllToolbarFilters}
                    >
                    Clear all
                    </Button>
                  ) : null}

                  <div className={classes.toolbarDivider} role="presentation" />

                  <div
                    className={classes.saveToolbarGroup}
                    role="group"
                    aria-label="Save search and result layout"
                  >
                    <Button
                      type="button"
                      variant="text"
                      className={classes.saveButton}
                      startIcon={(
                        <BookmarkIcon
                          {...COACH_PROFILE_FILTER_ICON_PROPS}
                          size={16}
                        />
                      )}
                      onClick={saveCurrentSearch}
                    >
                    Save
                    </Button>
                    {savedSearchCount > 0 && (
                      <>
                        <Button
                          type="button"
                          variant="text"
                          className={classes.savedSearchCountButton}
                          startIcon={(
                            <BookmarkIcon
                              {...COACH_PROFILE_FILTER_ICON_PROPS}
                              size={16}
                              fill="currentColor"
                            />
                          )}
                          endIcon={(
                            <ChevronDownIcon
                              {...COACH_PROFILE_FILTER_ICON_PROPS}
                              size={16}
                            />
                          )}
                          onClick={(event) => setSavedSearchAnchor(event.currentTarget)}
                          aria-label={`Open ${savedSearchCount} saved search`}
                        >
                          {savedSearchCount}
                        </Button>
                        <Menu
                          anchorEl={savedSearchAnchor}
                          open={Boolean(savedSearchAnchor)}
                          onClose={() => setSavedSearchAnchor(null)}
                          keepMounted
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          getContentAnchorEl={null}
                          PaperProps={{ className: classes.savedSearchMenuPaper }}
                        >
                          <Box className={classes.savedSearchMenuHeader}>
                            <Typography component="p" className={classes.savedSearchMenuEyebrow}>
                            Saved Searches
                            </Typography>
                          </Box>
                          {savedSearches.map((savedSearchState) => {
                            const savedSearchPersonaLabel = getJourneyPersona(savedSearchState.persona)?.heroLabel || 'Saved search'
                            const savedSearchLocationLabel = savedSearchState.placeName || ''
                            const savedSearchKey = JSON.stringify(savedSearchState)
                            return (
                              <MenuItem
                                key={savedSearchKey}
                                onClick={() => applySavedSearch(savedSearchState)}
                                className={classes.savedSearchMenuItem}
                              >
                                <div className={classes.savedSearchMenuItemContent}>
                                  <Typography component="span" className={classes.savedSearchMenuLabel}>
                                    <span>{savedSearchPersonaLabel}</span>
                                    {savedSearchLocationLabel ? (
                                      <>
                                        <span aria-hidden className={classes.savedSearchMenuSeparator}>•</span>
                                        <span>{savedSearchLocationLabel}</span>
                                      </>
                                    ) : null}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    className={classes.savedSearchRemoveButton}
                                    aria-label={`Remove saved search ${savedSearchPersonaLabel}`}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      event.preventDefault()
                                      removeSavedSearch(savedSearchState)
                                    }}
                                  >
                                    <XIcon
                                      {...COACH_PROFILE_FILTER_ICON_PROPS}
                                      size={16}
                                    />
                                  </IconButton>
                                </div>
                              </MenuItem>
                            )
                          })}
                        </Menu>
                      </>
                    )}
                    {isDesktop && (
                      <Button
                        type="button"
                        variant="text"
                        className={classes.layoutViewToggleButton}
                        startIcon={isMapViewSelected
                          ? (
                            <MapIcon
                              {...COACH_PROFILE_FILTER_ICON_PROPS}
                              size={16}
                            />
                          )
                          : (
                            <ListIcon
                              {...COACH_PROFILE_FILTER_ICON_PROPS}
                              size={16}
                            />
                          )}
                        onClick={toggleLayoutView}
                        aria-label={isMapViewSelected
                          ? 'Map view selected. Switch to list view without map'
                          : 'List view selected. Switch to map view with list and map'}
                      >
                        {isMapViewSelected ? 'Map' : 'List'}
                      </Button>
                    )}
                  </div>

                </div>
              )}
              {saveMessage && (
                <Typography color="primary" variant="body2" className={classes.saveMessage}>
                  {saveMessage}
                </Typography>
              )}
            </div>
          </div>
        </div>

        <div className={classes.contentArea}>
          <div
            className={classes.resultsColumn}
            style={{
              flex: resultsColumnFlex,
              ...(!isDesktop && mobileView !== 'results' ? { display: 'none' } : {}),
            }}
          >
            <div className={classes.resultsPanel}>
              <Box className={classes.resultsHeader}>
                <div>
                  <Typography variant="h5" component="h1">{resultsHeadline}</Typography>
                  <Typography className={classes.meta}>
                    {mapSummaryLabel}
                  </Typography>
                </div>
                {isMapUpdating && (
                  <Typography className={classes.updatingText}>
                      Updating results...
                  </Typography>
                )}
              </Box>

              {isViewingAwayFromSelectedArea && (
                <Box className={classes.notice}>
                  <Typography variant="body2">
                      Viewing results outside your selected area.
                  </Typography>
                </Box>
              )}

              <Box
                className={autoExpandNoticeCopy ? classes.notice : undefined}
                role="status"
                aria-live="polite"
              >
                {autoExpandNoticeCopy ? (
                  <Typography variant="body2">
                    {autoExpandNoticeCopy}
                  </Typography>
                ) : null}
              </Box>

              {errorMessage && (
                <Box pb={2}>
                  <Typography color="error">
                      We could not load offering results right now. {errorMessage}
                  </Typography>
                </Box>
              )}

              {(isLoading || isAutoExpanding) && !hasSearchResults && (
                <Box
                  display="flex"
                  justifyContent="center"
                  py={8}
                  role="status"
                  aria-live="polite"
                  aria-label="Updating search results"
                >
                  <CircularProgress color="secondary" />
                </Box>
              )}

              {!isLoading && !isAutoExpanding && !hasSearchResults && (
                <div className={classes.emptyState}>
                  {showEmptyRecovery ? (
                    <>
                      {renderEmptyStateIcon()}
                      <Typography variant="h4">{emptyStateContent.title}</Typography>
                      <Box pt={2}>
                        <Typography className={classes.meta}>{emptyStateContent.description}</Typography>
                      </Box>
                      <Box pt={3} className={classes.emptyActions}>
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.emptyPrimaryButton}
                          onClick={relaxFilters}
                        >
                          Show Me The Best Options
                        </Button>
                        <Button
                          variant="text"
                          color="primary"
                          className={classes.emptyTextActionButton}
                          onClick={openFiltersForAdjustment}
                        >
                          Adjust Filters
                        </Button>
                      </Box>
                    </>
                  ) : null}
                  {hasFallbackResults && (
                    <Box className={classes.fallbackSection}>
                      <Typography variant="h6" className={classes.fallbackSectionTitle}>
                          Closest matches near you
                      </Typography>
                      <div className={classes.fallbackRail}>
                        {fallbackOfferings.map((offering, index) => (
                          <div key={offering.objectID} className={classes.fallbackCard}>
                            <PgaCoachOfferingCard
                              position={index + 1}
                              offering={offering}
                              trackingSource="list"
                              journeyPersona={searchState.persona}
                              selectedSchedule={searchState.schedule}
                              onSelect={handleOfferingSelect}
                              openDetailsFromMapNonce={
                                mapOpenDetailsSignal?.offeringId === offering.objectID
                                  ? mapOpenDetailsSignal.nonce
                                  : undefined
                              }
                              isOfferingFavorite={savedIdSet.has(String(offering.objectID))}
                              onOfferingFavoriteToggle={() => handleOfferingFavoriteToggle(offering.objectID)}
                              onCoachProfileOpen={handleCoachProfileOpen}
                            />
                          </div>
                        ))}
                      </div>
                    </Box>
                  )}
                  {(showEmptyRecovery || hasFallbackResults) && renderEmptyStateDivider()}
                  {renderEmptyStateGuides()}
                  <Box pt={2}>
                    <Button
                      variant="text"
                      color="primary"
                      className={classes.emptyTextActionButton}
                      onClick={() => router.push('/coaches/search')}
                    >
                        Browse Coaches
                    </Button>
                  </Box>
                </div>
              )}

              {!isLoading && hasListRows && (
                <div className={classes.resultsList}>
                  {displayItems.map((item, index) => {
                    if (item.type === 'coach') {
                      const coach = item.data
                      return (
                        <ExternallyBookableCoachCard
                          key={coach.objectID}
                          coach={coach}
                          position={index + 1}
                        />
                      )
                    }
                    const offering = item.data
                    return (
                      <div
                        key={offering.objectID}
                        ref={(node) => {
                          if (!node) return
                          cardRefs.current[offering.objectID] = node
                        }}
                      >
                        <PgaCoachOfferingCard
                          position={index + 1}
                          offering={offering}
                          journeyPersona={searchState.persona}
                          selectedSchedule={searchState.schedule}
                          isHighlighted={selectedOfferingId === offering.objectID || hoveredOfferingId === offering.objectID}
                          onHoverStart={(hoveredOffering) => setHoveredOfferingId(hoveredOffering.objectID)}
                          onHoverEnd={() => setHoveredOfferingId(null)}
                          onSelect={handleOfferingSelect}
                          openDetailsFromMapNonce={
                            mapOpenDetailsSignal?.offeringId === offering.objectID
                              ? mapOpenDetailsSignal.nonce
                              : undefined
                          }
                          isOfferingFavorite={savedIdSet.has(String(offering.objectID))}
                          onOfferingFavoriteToggle={() => handleOfferingFavoriteToggle(offering.objectID)}
                          onCoachProfileOpen={handleCoachProfileOpen}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
              {!isLoading && hasListRows && (
                <div
                  ref={results.hasMore ? loadMoreSentinelRef : undefined}
                  className={classes.loadMoreZone}
                  aria-live="polite"
                  aria-label={isLoadingMore ? 'Loading more results' : undefined}
                >
                  {isLoadingMore && <CircularProgress size={22} color="secondary" />}
                </div>
              )}
            </div>
          </div>

          {(isDesktop ? showDesktopMap : mobileView === 'map') && (
            <div
              className={classes.mapColumn}
            >
              <div className={classes.mapPanel}>
                <PgaCoachSearchMap
                  key={`coach-search-map-${mapResetKey}`}
                  offerings={visibleOfferings}
                  initialCenter={selectedLocation}
                  selectedOfferingId={selectedOfferingId}
                  pinPopupAnchorOfferingId={mapPinPopupAnchorOfferingId}
                  hoveredOfferingId={hoveredOfferingId}
                  onSelectOffering={handleMapOfferingSelect}
                  onViewportChange={setMapViewport}
                  onDragEndCenterChange={handleMapDragEndCenterChange}
                  onInteractionStateChange={setIsMapUpdating}
                  autoFitBounds={!suppressMapAutoFit}
                  compactOverlay
                />
              </div>
            </div>
          )}
        </div>
      </Container>
      {coachInfoPanelEnabled && selectedCoachProfile && (
        <PgaCoachInfoPanel
          coach={selectedCoachProfile.coach}
          locationLabel={selectedCoachProfile.locationLabel}
          profileUrl={selectedCoachProfile.profileUrl}
          onClose={handleCoachProfileClose}
          onCtaClick={handleCoachProfileCtaClick}
          layout={isDesktop ? 'sidebar' : 'fullscreen'}
          panelTop={navHeight}
        />
      )}
    </div>
  )
}

CoachFilterMenu.propTypes = {
  classes: PropTypes.object.isRequired,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  selectedId: PropTypes.string.isRequired,
  onSelectId: PropTypes.func.isRequired,
  paperClassName: PropTypes.string,
  paperStyle: PropTypes.object,
  dividerAfterIndices: PropTypes.arrayOf(PropTypes.number),
}

PgaCoachSearch.propTypes = {
  initialSearchState: PropTypes.object,
  initialResults: PropTypes.shape({
    hits: PropTypes.array,
    allHits: PropTypes.array,
    totalHits: PropTypes.number,
    totalPages: PropTypes.number,
    page: PropTypes.number,
    perPage: PropTypes.number,
    hasMore: PropTypes.bool,
    nextPage: PropTypes.number,
  }),
  initialError: PropTypes.string,
}

export default PgaCoachSearch
